/**
 * An agent eval's tools, from the studio page. Everything goes to the chat's
 * host (`DrafterHostDO`), which serves them only while that eval is parked;
 * a tool the host answers with its page (the sandbox and checkpoint tools,
 * jig tools) runs here, through `runPageTool`.
 */
import type {
  AgentToolResult,
  AgentToolsApi,
} from "playground/agent-tools/contract";
import { z } from "zod";

const pageAnswer = z.object({
  page: z.object({ appId: z.string(), tool: z.string() }),
});

export type PageTool = z.infer<typeof pageAnswer>["page"];

export function bearAgentTools(
  chat: { projectId: string; chatId: string; toolCallId: string },
  runPageTool: (
    page: PageTool,
    input: Record<string, unknown> | undefined,
  ) => Promise<AgentToolResult>,
  fetchFn: typeof fetch = fetch,
): AgentToolsApi {
  const post = async (method: string, args: unknown[]): Promise<unknown> => {
    const response = await fetchFn(
      `/api/agents/drafter-host/${chat.projectId}:${chat.chatId}/agent-tools`,
      {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ toolCallId: chat.toolCallId, method, args }),
      },
    );
    const body: { result?: unknown; error?: string } = await response.json();
    if (!response.ok)
      throw new Error(
        body.error ?? `Agent tools request failed (${response.status}).`,
      );
    return body.result;
  };
  // The host applies the same method to its `AgentToolsApi`, so each answer
  // is that method's own result.
  const forward = <K extends keyof AgentToolsApi>(method: K) =>
    ((...args: unknown[]) => post(method, args)) as AgentToolsApi[K];
  return {
    read: forward("read"),
    write: forward("write"),
    edit: forward("edit"),
    bash: forward("bash"),
    list: forward("list"),
    search: forward("search"),
    describe: forward("describe"),
    async call(selector, input) {
      const answer = await post("call", [selector, input]);
      const page = pageAnswer.safeParse(answer);
      return page.success
        ? runPageTool(page.data.page, input)
        : (answer as AgentToolResult);
    },
  };
}
